import dayjs from 'dayjs'
import type { BalanceSlot } from '@sportninja/common/services/teamBalance'
import type { BalanceSheetRow } from '../types/balanceGrid'
import {
  isCandidateTableRow,
  isCurrentOfficialLine,
  isInvitedSlot,
  isOpenPlayerSlot,
} from './balanceRowKind'

/**
 * The sheet's DEFAULT row order, per registration:
 *
 *   TEAM REPS  the registering rep, then the other current officials' lines in the
 *              order they were opened, then the officials with no line yet by name
 *   JOINED     the players, and former officials' lines, in the order they joined
 *   INVITED    slots held for invited players, in the order the invites were sent
 *   OPEN       the slots anyone can still register into, in the server's order
 *   CANCELLED  in the order they joined
 *
 * SN-8467 (Jeff, Oct 1) — "Team Reps first". This supersedes Sep 24's single join
 * order, under which a rep who had not paid yet — a $0 candidate row — sat after the
 * open slots, and an official's line among the players by when it was opened.
 *
 * Within the bands the Sep 24 rule stands: the server sends slots by
 * `team_registration_slot.id`, and occupancy does not follow the ids
 * (`attachPlayerToNextOpenSlot` fills the LOWEST open slot in place and `releaseSlot`
 * empties one where it stands), so each row is placed by its occupant's own
 * `joined_at`: a player's registration, or when the official's line was opened.
 *
 * The footer's "n of m filled" counts players only. This is DISPLAY ONLY: slot
 * identity is the uid, never the row's position, and every column stays sortable —
 * clicking a header still overrides this order the way it always did.
 */
export const ROW_GROUP_TEAM_REPS = 0
export const ROW_GROUP_JOINED = 1
export const ROW_GROUP_INVITED = 2
export const ROW_GROUP_OPEN = 3
export const ROW_GROUP_CANCELLED = 4

/**
 * Which display band a slot belongs to: a current official's line is a team rep's;
 * a filled player slot, and a former official's line (it keeps its history and takes
 * no new money), has joined; an empty player slot is still to fill.
 *
 * Cancelled is checked FIRST and is its own band even though a cancelled slot still
 * has an occupant: the registration is dead, the slot is excluded from the split, and
 * it is hidden entirely unless "Display Cancelled" is on — so a withdrawn player must
 * not push a slot the rep can still fill further down the sheet. A cancelled official
 * line, the rep's own included, joins it for the same reason.
 *
 * Jeff, Oct 1 — a slot held for an invited player is its own band, between the
 * players who joined and the open slots: someone is expected in it. A Player Finder
 * slot needs no case of its own — it is an open slot of another type.
 */
export const slotRowGroup = (slot: BalanceSlot): number => {
  if (slot.status === 'cancelled') {
    return ROW_GROUP_CANCELLED
  }
  if (isCurrentOfficialLine(slot)) {
    return ROW_GROUP_TEAM_REPS
  }
  if (isInvitedSlot(slot)) {
    return ROW_GROUP_INVITED
  }

  return isOpenPlayerSlot(slot) ? ROW_GROUP_OPEN : ROW_GROUP_JOINED
}

/**
 * The band of any sheet row. A candidate — a team official with no line yet ($0,
 * "Show: All" only; a Daysmart manager too) — is a team rep: typing an amount opens
 * their line, which keeps them in the band.
 */
export const sheetRowGroup = (row: BalanceSheetRow): number =>
  isCandidateTableRow(row) ? ROW_GROUP_TEAM_REPS : slotRowGroup(row.slot)

/**
 * Inside TEAM REPS: the registering rep (0) — with a line or still a $0 candidate —
 * then the other officials' lines (1), then the other candidates (2).
 */
const teamRepRank = (row: BalanceSheetRow): number => {
  if (isCandidateTableRow(row)) {
    return row.candidate.is_registering_rep ? 0 : 2
  }

  return row.slot.official?.is_registering_rep === true ? 0 : 1
}

/**
 * When a row's occupant joined, in ms. A missing or unparseable time (an older api2,
 * an open slot) is +Infinity, so it sorts after every dated row — never NaN, which
 * would make the comparator inconsistent.
 */
export const joinedAtMs = (slot: Pick<BalanceSlot, 'joined_at'>): number => {
  if (!slot.joined_at) {
    return Number.POSITIVE_INFINITY
  }
  const parsed = dayjs(slot.joined_at)

  return parsed.isValid() ? parsed.valueOf() : Number.POSITIVE_INFINITY
}

/** When an invited slot's invite was sent, in ms; +Infinity when unknown (see joinedAtMs). */
export const invitedAtMs = (slot: Pick<BalanceSlot, 'invitee'>): number => {
  const sentAt = slot.invitee?.invited_at
  if (!sentAt) {
    return Number.POSITIVE_INFINITY
  }
  const parsed = dayjs(sentAt)

  return parsed.isValid() ? parsed.valueOf() : Number.POSITIVE_INFINITY
}

/**
 * Earlier first. Compared, never subtracted: two undated rows are ∞ − ∞, which is
 * NaN, and a NaN from a comparator breaks the whole sort.
 */
const compareTimes = (left: number, right: number): number =>
  left < right ? -1 : left > right ? 1 : 0

/**
 * Candidates: by name, then by the person. Two rows of one person can only come from
 * the same payload twice, and then the incoming order decides.
 */
const compareCandidates = (a: BalanceSheetRow, b: BalanceSheetRow): number => {
  if (!isCandidateTableRow(a) || !isCandidateTableRow(b)) {
    return 0
  }

  return (
    a.candidate.name.localeCompare(b.candidate.name, undefined, {
      sensitivity: 'base',
    }) || a.candidate.payer_user_id.localeCompare(b.candidate.payer_user_id)
  )
}

interface DecoratedRow<T> {
  row: T
  index: number
  group: number
  /** When the occupant joined — or, in INVITED, when the invite was sent. */
  joined: number
  /** `teamRepRank` inside TEAM REPS; 0 in every other band. */
  repRank: number
}

const compareWithinGroup = <T extends BalanceSheetRow>(
  a: DecoratedRow<T>,
  b: DecoratedRow<T>
): number => {
  switch (a.group) {
    case ROW_GROUP_TEAM_REPS:
      // The registering rep, then lines by when they were opened, then candidates
      // by name. A line sorts before a candidate of the same rank; a candidate's
      // time is ∞ and a line's name is not compared, so each pair uses its own key.
      return (
        a.repRank - b.repRank ||
        Number(isCandidateTableRow(a.row)) -
          Number(isCandidateTableRow(b.row)) ||
        compareTimes(a.joined, b.joined) ||
        compareCandidates(a.row, b.row)
      )
    case ROW_GROUP_JOINED:
    case ROW_GROUP_INVITED:
    case ROW_GROUP_CANCELLED:
      return compareTimes(a.joined, b.joined)
    default:
      // Open slots keep the server's own order.
      return 0
  }
}

/**
 * Order one registration's rows for display, banded as above:
 *
 *   TEAM REPS  registering rep, then lines by `joined_at`, then candidates by name
 *   JOINED     `joined_at` ascending
 *   INVITED    `invitee.invited_at` ascending
 *   OPEN       the server's order
 *   CANCELLED  `joined_at` ascending
 *
 * Every tie keeps the incoming (server) order: two players who joined in the same
 * second, rows with no time, and the open slots. The opaque slot uid is never
 * compared.
 *
 * Decorate-sort-undecorate rather than a bare `.sort()`: the time is parsed once per
 * row rather than once per comparison, and the incoming index is stated as the final
 * tie-break instead of leaning on sort stability.
 *
 * Returns a new array — the input's slots belong to the react-query cache.
 */
export const orderSheetRows = <T extends BalanceSheetRow>(rows: T[]): T[] =>
  rows
    .map((row, index): DecoratedRow<T> => {
      const group = sheetRowGroup(row)

      return {
        row,
        index,
        group,
        joined: isCandidateTableRow(row)
          ? Number.POSITIVE_INFINITY
          : group === ROW_GROUP_INVITED
          ? invitedAtMs(row.slot)
          : joinedAtMs(row.slot),
        repRank: group === ROW_GROUP_TEAM_REPS ? teamRepRank(row) : 0,
      }
    })
    .sort(
      (a, b) =>
        a.group - b.group || compareWithinGroup(a, b) || a.index - b.index
    )
    .map(({ row }) => row)
